import { Listing } from '../api';
import { formatListingPrice } from './listingPricing';

const DELIVERY_TIME_LABELS: Record<string, string> = {
  '1-2': '1–2 hours',
  '3-6': '3–6 hours',
  '24': 'Within 24 hours',
};

const DELIVERY_HEADLINES: Record<string, string> = {
  '1-2': 'Delivered today',
  '3-6': 'Same-day delivery',
  '24': 'Delivered within 24 hours',
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export type StoreHoursEntry = { open: string; close: string; closed: boolean };
export type StoreHoursSource = {
  hours?: string | Record<string, StoreHoursEntry> | null;
  open_time?: string | null;
  close_time?: string | null;
};

export function formatStoreTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

export function parseStoreHours(store: StoreHoursSource | null | undefined): Record<string, StoreHoursEntry> | null {
  if (!store) return null;
  if (store.hours) {
    if (typeof store.hours === 'string') {
      try {
        return JSON.parse(store.hours);
      } catch {
        return null;
      }
    }
    if (typeof store.hours === 'object') return store.hours;
  }
  if (store.open_time && store.close_time) {
    return Object.fromEntries(
      DAY_NAMES.map((day) => [day, { open: store.open_time!, close: store.close_time!, closed: false }])
    );
  }
  return null;
}

function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function getTodayStoreHours(store: StoreHoursSource | null | undefined): StoreHoursEntry | null {
  const hoursObj = parseStoreHours(store);
  if (!hoursObj) return null;
  const todayName = DAY_NAMES[new Date().getDay()];
  return hoursObj[todayName] ?? null;
}

export function getStoreOpenStatus(store: StoreHoursSource | null | undefined): {
  isOpen: boolean | null;
  minutesUntilClose: number | null;
  todayOpen: string | null;
  todayClose: string | null;
} {
  const today = getTodayStoreHours(store);
  if (!today) {
    if (store?.open_time && store?.close_time) {
      const now = new Date();
      const current = now.getHours() * 60 + now.getMinutes();
      const open = parseTimeToMinutes(store.open_time);
      const close = parseTimeToMinutes(store.close_time);
      const isOpen = current >= open && current < close;
      return {
        isOpen,
        minutesUntilClose: isOpen ? close - current : null,
        todayOpen: store.open_time,
        todayClose: store.close_time,
      };
    }
    return { isOpen: null, minutesUntilClose: null, todayOpen: null, todayClose: null };
  }
  if (today.closed) {
    return { isOpen: false, minutesUntilClose: null, todayOpen: today.open, todayClose: today.close };
  }
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  const open = parseTimeToMinutes(today.open);
  const close = parseTimeToMinutes(today.close);
  const isOpen = current >= open && current < close;
  return {
    isOpen,
    minutesUntilClose: isOpen ? close - current : null,
    todayOpen: today.open,
    todayClose: today.close,
  };
}

export function orderWithinLabel(store: StoreHoursSource | null | undefined): string | null {
  const { isOpen, minutesUntilClose } = getStoreOpenStatus(store);
  if (isOpen !== true || minutesUntilClose == null || minutesUntilClose <= 0) return null;
  const h = Math.floor(minutesUntilClose / 60);
  const m = minutesUntilClose % 60;
  if (h > 0 && m > 0) return `Order within ${h}h ${m}m`;
  if (h > 0) return `Order within ${h}h`;
  return `Order within ${m}m`;
}

export function sellerHoursSourceFromListing(listing: Listing): StoreHoursSource | null {
  if (listing.store_id) return null;
  const backup =
    listing.shop_store_close_time?.trim().startsWith('{') ? listing.shop_store_close_time : null;
  const hours = listing.seller_hours ?? backup;
  const open = listing.seller_open_time;
  const close = listing.seller_close_time;
  if (!hours && !open && !close) return null;
  return {
    hours: hours ?? null,
    open_time: open ?? null,
    close_time: close ?? null,
  };
}

export function hoursClosedLabel(source: StoreHoursSource | null | undefined, kind: 'store' | 'seller' = 'store'): string | null {
  const status = getStoreOpenStatus(source);
  if (status.isOpen !== false) return null;
  if (status.todayOpen && status.todayClose) {
    const prefix = kind === 'seller' ? 'Not available today' : 'Closed today';
    return `${prefix} · Hours ${formatStoreTime(status.todayOpen)} – ${formatStoreTime(status.todayClose)}`;
  }
  return kind === 'seller' ? 'Not available' : 'Store closed';
}

export function hoursScheduleSubtitle(
  source: StoreHoursSource | null | undefined
): string | null {
  const hoursObj = parseStoreHours(source);
  if (!hoursObj) return null;

  const now = new Date();
  const startDay = now.getDay();
  const current = now.getHours() * 60 + now.getMinutes();

  for (let offset = 0; offset < 7; offset++) {
    const dayIndex = (startDay + offset) % 7;
    const dayName = DAY_NAMES[dayIndex];
    const entry = hoursObj[dayName];
    if (!entry || entry.closed || !entry.open || !entry.close) continue;

    const openMin = parseTimeToMinutes(entry.open);
    if (offset === 0 && current >= openMin) {
      const closeMin = parseTimeToMinutes(entry.close);
      if (current < closeMin) return null;
      continue;
    }

    const times = `${formatStoreTime(entry.open)} – ${formatStoreTime(entry.close)}`;
    if (offset === 0) return `Today · ${times}`;
    if (offset === 1) return `Tomorrow · ${times}`;
    return `${dayName} · ${times}`;
  }
  return null;
}

export function hoursNextAvailableLabel(
  source: StoreHoursSource | null | undefined,
  kind: 'store' | 'seller' = 'store'
): string | null {
  const hoursObj = parseStoreHours(source);
  if (!hoursObj) return null;

  const now = new Date();
  const startDay = now.getDay();
  const current = now.getHours() * 60 + now.getMinutes();

  for (let offset = 0; offset < 7; offset++) {
    const dayIndex = (startDay + offset) % 7;
    const dayName = DAY_NAMES[dayIndex];
    const entry = hoursObj[dayName];
    if (!entry || entry.closed || !entry.open) continue;

    const openMin = parseTimeToMinutes(entry.open);
    if (offset === 0 && current >= openMin) {
      const closeMin = parseTimeToMinutes(entry.close);
      if (current < closeMin) return null;
      continue;
    }

    const hoursRange =
      entry.close && entry.open
        ? `Hours ${formatStoreTime(entry.open)} – ${formatStoreTime(entry.close)}`
        : null;
    let prefix: string;
    if (offset === 0) {
      prefix = kind === 'seller' ? 'Available today' : 'Opens today';
    } else if (offset === 1) {
      prefix = kind === 'seller' ? 'Available tomorrow' : 'Opens tomorrow';
    } else {
      prefix = kind === 'seller' ? `Available ${dayName}` : `Opens ${dayName}`;
    }
    return hoursRange ? `${prefix} · ${hoursRange}` : prefix;
  }
  return null;
}

/** Turn "1–2 hours" into "1–2 hr delivery" for closed-hours headlines. */
export function scheduleDeliveryPhrase(deliveryTime: string): string {
  let t = deliveryTime.trim();
  t = t.replace(/^within\s+/i, '');
  t = t.replace(/\s*hours?\s*$/i, '');
  return `${t} hr delivery`;
}

/** Closed-hours headline: "Order now · 1–2 hr delivery when available". */
export function hoursScheduleTitle(
  kind: 'store' | 'seller' = 'store',
  deliveryTime?: string | null
): string {
  const when = kind === 'seller' ? 'when available' : 'when open';
  const time = deliveryTime?.trim();
  if (time) {
    return `Order now · ${scheduleDeliveryPhrase(time)} ${when}`;
  }
  return `Order now · delivers ${when}`;
}

/** Open-hours title with window folded in: "Delivered today · 1–2 hours". */
export function listingDeliveryTitleWithTime(
  title: string,
  deliveryTime?: string | null
): string {
  const time = deliveryTime?.trim();
  if (!time) return title;
  if (title.toLowerCase().includes('24') && time.toLowerCase().includes('24')) {
    return title;
  }
  return `${title} · ${time}`;
}

export function hoursUnavailableDetailLabel(
  source: StoreHoursSource | null | undefined,
  kind: 'store' | 'seller' = 'store'
): string | null {
  if (!source) return null;
  const status = getStoreOpenStatus(source);
  if (status.isOpen !== false) return null;
  return (
    hoursScheduleSubtitle(source) ??
    (kind === 'seller' ? 'Not available' : 'Store closed')
  );
}

export function hoursStatusBadgeLabel(
  source: StoreHoursSource | null | undefined,
  kind: 'store' | 'seller' = 'store'
): string | null {
  const status = getStoreOpenStatus(source);
  if (status.isOpen === true) return kind === 'seller' ? 'Available' : 'Open';
  if (status.isOpen === false) return kind === 'seller' ? 'Not available' : 'Closed';
  return null;
}

export function storeClosedLabel(store: StoreHoursSource | null | undefined): string | null {
  return hoursUnavailableDetailLabel(store, 'store');
}

export function deliveryTimeLabel(listing: Listing): string | null {
  if (listing.delivery_time?.display) return listing.delivery_time.display;
  const opt = listing.delivery_time_option;
  if (opt && DELIVERY_TIME_LABELS[opt]) return DELIVERY_TIME_LABELS[opt];
  return null;
}

export function deliveryHeadline(listing: Listing): string {
  const opt = listing.delivery_time_option;
  if (opt && DELIVERY_HEADLINES[opt]) return DELIVERY_HEADLINES[opt];
  return 'Delivered to your door';
}

/** Headline for the delivery card — individual sellers within hours show "Delivered today". */
export function listingDeliveryTitle(
  listing: Listing,
  options: {
    isStoreListing: boolean;
    hoursSource: StoreHoursSource | null | undefined;
    isOpen: boolean | null;
  }
): string {
  const { isStoreListing, hoursSource, isOpen } = options;
  if (!isStoreListing && hoursSource && isOpen === true) return 'Delivered today';
  if (!isStoreListing && !hoursSource) return 'Local delivery';
  return deliveryHeadline(listing);
}

export type DeliveryFeeTier = { from: number; to: number; fee: number };

export function parseDeliveryFeeTiers(listing: Listing): DeliveryFeeTier[] | null {
  if (!listing.delivery_fee_tiers) return null;
  try {
    const raw = JSON.parse(listing.delivery_fee_tiers) as Array<{
      from?: number | string;
      to?: number | string;
      fee?: number | string;
    }>;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return raw
      .map((t) => ({
        from: Number(t.from) || 0,
        to: Number(t.to) || 0,
        fee: Number(t.fee) || 0,
      }))
      .sort((a, b) => a.from - b.from);
  } catch {
    return null;
  }
}

function formatTierFee(fee: number): string {
  return fee === 0 ? 'Free' : formatListingPrice(fee);
}

export function formatDeliveryFeeTierLine(tier: DeliveryFeeTier): string {
  const toLabel = tier.to % 1 === 0 ? tier.to.toString() : tier.to.toFixed(1);
  const fromLabel = tier.from % 1 === 0 ? tier.from.toString() : tier.from.toFixed(1);
  if (tier.from === 0) {
    return `0–${toLabel} mi: ${formatTierFee(tier.fee)}`;
  }
  return `${fromLabel}–${toLabel} mi: ${formatTierFee(tier.fee)}`;
}

/** Lines for delivery fee tooltip — full tiers for distance-based, single line for fixed fee. */
export function deliveryFeeTooltipLines(listing: Listing): string[] | null {
  const tiers = parseDeliveryFeeTiers(listing);
  if (tiers && tiers.length > 0) {
    return tiers.map(formatDeliveryFeeTierLine);
  }
  const fixed = listing.shipping_fee;
  if (fixed != null && Number(fixed) >= 0) {
    return [`Delivery fee: ${formatTierFee(Number(fixed))}`];
  }
  return null;
}

export function deliveryFeeLabel(listing: Listing): string | null {
  const fixed = listing.shipping_fee;
  if (fixed != null && Number(fixed) > 0) {
    return `Delivery from ${formatListingPrice(Number(fixed))}`;
  }
  const tiers = parseDeliveryFeeTiers(listing);
  if (tiers) {
    const fees = tiers.map((t) => t.fee).filter((f) => f > 0);
    if (fees.length > 0) {
      return `Delivery from ${formatListingPrice(Math.min(...fees))}`;
    }
    if (tiers.some((t) => t.fee === 0)) {
      return 'Free delivery available';
    }
  }
  return null;
}

export function sellerAreaLabel(listing: Listing): string | null {
  const city = listing.city_name;
  const state = listing.state_abbreviation || listing.state_name;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  return null;
}

export function isStoreOpenNow(store: StoreHoursSource | null | undefined): boolean | null {
  return getStoreOpenStatus(store).isOpen;
}
